import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FileText, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { formatDateTimeInZambia, formatMonthLabel } from "@/lib/zambia-time";

interface LogRow {
  id: string;
  inventory_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_by: string;
  changed_at: string;
  inventory?: { name: string } | null;
}

// Reads from inventory_audit_log (stock_level + buying_price changes). Realtime subscription below refreshes on INSERT.
function fetchLogs(): Promise<LogRow[]> {
  return supabase
    .from("inventory_audit_log")
    .select("id, inventory_id, field_name, old_value, new_value, changed_by, changed_at, inventory(name)")
    .order("changed_at", { ascending: false })
    .limit(500)
    .then(({ data, error }) => (error ? [] : (data ?? []) as LogRow[]));
}

async function fetchChangedByNames(userIds: string[]): Promise<Record<string, string>> {
  if (userIds.length === 0) return {};
  const { data } = await supabase
    .from("profiles")
    .select("user_id, full_name, email")
    .in("user_id", userIds);
  const map: Record<string, string> = {};
  (data ?? []).forEach((r: { user_id: string; full_name: string | null; email: string | null }) => {
    const name = r.full_name?.trim() || r.email?.trim() || r.user_id.slice(0, 8);
    map[r.user_id] = name;
  });
  return map;
}

export default function SystemLogsPage() {
  const { isFounder, role } = useAuth();
  const isAdmin = role === "admin" || isFounder;
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [changedByNames, setChangedByNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const data = await fetchLogs();
    setLogs(data);
    const userIds = [...new Set(data.map((r) => r.changed_by))];
    const names = await fetchChangedByNames(userIds);
    setChangedByNames(names);
    setLoading(false);
  }, []);

  const logsByMonth = useMemo(() => {
    const map = new Map<string, LogRow[]>();
    logs.forEach((log) => {
      const key = log.changed_at.slice(0, 7);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(log);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([monthKey, rows]) => ({ monthKey, label: formatMonthLabel(monthKey + "-01"), rows }));
  }, [logs]);

  useEffect(() => {
    if (!isAdmin) return;
    loadLogs();
  }, [isAdmin, loadLogs]);

  // Real-time: when inventory_audit_log gets new rows (trigger on inventory UPDATE), append to list
  useEffect(() => {
    if (!isAdmin) return;
    const channel = supabase
      .channel("inventory_audit_log_live")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "inventory_audit_log" },
        () => { loadLogs(); }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, loadLogs]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Access restricted to admins.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-bold">System Logs</h2>
        <p className="text-sm text-muted-foreground">
          Read-only audit trail: every change to stock_level (current_stock) or buying_price is logged with timestamp and the user who made the change.
        </p>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-primary" />
              Inventory Change Log
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadLogs} disabled={loading} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">No inventory changes logged yet.</p>
          ) : (
            <div className="space-y-2">
              {logsByMonth.map(({ monthKey, label, rows }) => (
                <Collapsible key={monthKey} defaultOpen={false}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="group flex w-full items-center gap-2 py-2 px-2 rounded-md hover:bg-muted/50 text-left font-medium text-sm"
                    >
                      <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
                      <span>{label}</span>
                      <span className="text-muted-foreground font-normal">({rows.length} change{rows.length === 1 ? "" : "s"})</span>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-2 font-semibold">Time</th>
                            <th className="text-left py-2 px-2 font-semibold">Item</th>
                            <th className="text-left py-2 px-2 font-semibold">Field</th>
                            <th className="text-left py-2 px-2 font-semibold">Old</th>
                            <th className="text-left py-2 px-2 font-semibold">New</th>
                            <th className="text-left py-2 px-2 font-semibold">Changed by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <tr key={row.id} className="border-b border-border/40">
                              <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                                {formatDateTimeInZambia(row.changed_at)}
                              </td>
                              <td className="py-2 px-2">{row.inventory?.name ?? row.inventory_id}</td>
                              <td className="py-2 px-2">{row.field_name}</td>
                              <td className="py-2 px-2 font-mono text-xs">{row.old_value ?? "—"}</td>
                              <td className="py-2 px-2 font-mono text-xs">{row.new_value ?? "—"}</td>
                              <td className="py-2 px-2 text-muted-foreground">
                                {changedByNames[row.changed_by] ?? "Unknown"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
